import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Box, ToggleButton, ToggleButtonGroup, Divider } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { useEffect } from 'react';

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
}

export default function RichTextEditor({ value, onChange, label }: RichTextEditorProps) {
    const editor = useEditor({
        extensions: [StarterKit, Underline],
        content: value,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value);
        }
    }, [value, editor]);

    if (!editor) return null;

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                },
                '&:focus-within': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                },
            }}
        >
            {label && (
                <Box
                    sx={{
                        px: 1,
                        pt: 0.5,
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                    }}
                >
                    {label}
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <ToggleButtonGroup size="small">
                    <ToggleButton
                        value="bold"
                        selected={editor.isActive('bold')}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                    >
                        <FormatBoldIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                        value="italic"
                        selected={editor.isActive('italic')}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                    >
                        <FormatItalicIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                        value="underline"
                        selected={editor.isActive('underline')}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                    >
                        <FormatUnderlinedIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                        value="strike"
                        selected={editor.isActive('strike')}
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                    >
                        <StrikethroughSIcon fontSize="small" />
                    </ToggleButton>
                </ToggleButtonGroup>

                <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

                <ToggleButtonGroup size="small">
                    <ToggleButton
                        value="h1"
                        selected={editor.isActive('heading', { level: 1 })}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                    >
                        H1
                    </ToggleButton>
                    <ToggleButton
                        value="h2"
                        selected={editor.isActive('heading', { level: 2 })}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                    >
                        H2
                    </ToggleButton>
                    <ToggleButton
                        value="h3"
                        selected={editor.isActive('heading', { level: 3 })}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                    >
                        H3
                    </ToggleButton>
                </ToggleButtonGroup>

                <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

                <ToggleButtonGroup size="small">
                    <ToggleButton
                        value="bulletList"
                        selected={editor.isActive('bulletList')}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                    >
                        <FormatListBulletedIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                        value="orderedList"
                        selected={editor.isActive('orderedList')}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    >
                        <FormatListNumberedIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                        value="blockquote"
                        selected={editor.isActive('blockquote')}
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    >
                        <FormatQuoteIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                        value="codeBlock"
                        selected={editor.isActive('codeBlock')}
                        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    >
                        <CodeIcon fontSize="small" />
                    </ToggleButton>
                </ToggleButtonGroup>

                <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

                <ToggleButtonGroup size="small">
                    <ToggleButton
                        value="undo"
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                    >
                        <UndoIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                        value="redo"
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                    >
                        <RedoIcon fontSize="small" />
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box
                sx={{
                    px: 2,
                    py: 1,
                    minHeight: 200,
                    '& .ProseMirror': {
                        outline: 'none',
                        minHeight: 200,
                        '& p': { margin: '0.5em 0' },
                        '& h1, & h2, & h3': { margin: '0.75em 0 0.25em' },
                        '& ul, & ol': { paddingLeft: '1.5em' },
                        '& blockquote': {
                            borderLeft: '3px solid',
                            borderColor: 'divider',
                            pl: 2,
                            ml: 0,
                            color: 'text.secondary',
                        },
                        '& pre': {
                            backgroundColor: 'grey.100',
                            borderRadius: 1,
                            p: 1.5,
                            fontFamily: 'monospace',
                        },
                    },
                }}
            >
                <EditorContent editor={editor} />
            </Box>
        </Box>
    );
}
